import { UIEvent } from 'react';

import { Overlay, Portal } from 'ui/components/primitives';

import type { ModalProps } from './Modal.d';
import { CloseButton, Footer, Variant } from './components';
import { MODAL_CONST } from './Modal.enums';
import { Modal } from './Modal.styles';

const ModalBase = ({
  align = 'center-center',
  cancelAction,
  cancelLabel,
  children,
  closeButton = true,
  closeButtonLabel,
  context = 'contextual',
  footer,
  header,
  onClose,
  overapp = false,
  preventClose = false,
  primaryAction,
  primaryLabel,
  radius = 'rounded',
  secondaryAction,
  secondaryLabel,
  size = 'medium',
  sizeButton = 'small',
  sizeIcon = 's',
  stretch = false,
  variant = Variant.DEFAULT,
  visible,
  isExtendedCaptation,
  ...rest
}: ModalProps) => {
  const handleClose = (e: UIEvent): void => {
    if (e.target === e.currentTarget && !preventClose && onClose) {
      onClose(e);
    }
  };

  const ModalComponent = () => (
    <Modal.Component
      data-align={align}
      data-testid={MODAL_CONST.TEST_ID}
      data-visible={visible}
      onClick={handleClose}
      {...rest}
    >
      <Modal.Dialog data-context={context} data-size={size} data-stretch={stretch}>
        <Modal.Card
          data-align={align}
          data-context={context}
          data-radius={radius}
          footer={
            footer ||
            (variant !== Variant.DEFAULT && (
              <Footer
                cancelAction={cancelAction}
                cancelLabel={cancelLabel}
                primaryAction={primaryAction}
                primaryLabel={primaryLabel}
                secondaryAction={secondaryAction}
                secondaryLabel={secondaryLabel}
                variant={variant}
              />
            ))
          }
          header={<Modal.Header>{header}</Modal.Header>}
          spacing="large"
        >
          {closeButton && onClose && (
            <CloseButton
              aria-label={closeButtonLabel || 'close modal'}
              onClick={onClose}
              sizeButton={sizeButton}
              sizeIcon={sizeIcon}
            />
          )}
          {children}
        </Modal.Card>
      </Modal.Dialog>
    </Modal.Component>
  );

  if (overapp) {
    return (
      <Portal>
        <Overlay onClick={handleClose} visible={visible} />
        {visible && <ModalComponent />}
      </Portal>
    );
  }

  return (
    <div data-testid={MODAL_CONST.WRAPPER_TESTID}>
      <Overlay onClick={handleClose} visible={visible} isExtendedCaptation={isExtendedCaptation} />
      {visible && <ModalComponent />}
    </div>
  );
};

export default ModalBase;
